import { format, formatDistanceToNow } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { State } from 'store'
import Button from 'uiComponents/buttons'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { ReactTable } from 'uiComponents/table/reactTable'
import { TableColumn } from 'uiComponents/table/reactTable/interface'
import { TooltipWrapper } from 'uiComponents/tooltip.new'
import { LucideIcon } from 'utils/lucideIcon'
import { ChipList } from './components/chipList'
import { CreateServiceAppModal } from './components/createServiceAppModal'
import { RowConfirmActions } from './components/rowConfirmActions'
import { SecretRevealModal } from './components/secretRevealModal'
import {
    useCreateServiceAppMutation,
    useGetServiceAppsQuery,
    useRevokeServiceAppMutation,
    useRotateServiceAppSecretMutation,
} from './reduxQuery'
import { NewServiceApp, SERVICE_APP_LIMIT, ServiceApp, ServiceAppWithSecret } from './schema'
import './styles.scss'
import { useTranslation } from 'translations'

const errorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error && error.message ? error.message : fallback

interface ServiceAppsSectionProps {
    setActiveSection?: (section: string) => void
}

const ServiceAppsSection: React.FC<ServiceAppsSectionProps> = ({ setActiveSection }) => {
    const location = useLocation()
    const history = useHistory()
    const { t } = useTranslation()
    const user = useSelector((state: State) => state.auth.user)
    const activeAccount = useSelector((state: State) => state.preferences.activeAccount)
    const { data: apps = [], isLoading, isError, refetch } = useGetServiceAppsQuery()
    const [createServiceApp, { isLoading: creating }] = useCreateServiceAppMutation()
    const [rotateSecret] = useRotateServiceAppSecretMutation()
    const [revokeServiceApp] = useRevokeServiceAppMutation()

    const [creatingApp, setCreatingApp] = useState(false)
    const [revealed, setRevealed] = useState<{ app: ServiceAppWithSecret; reason: 'created' | 'rotated' } | null>(null)

    useEffect(() => {
        setActiveSection?.('service_apps')
    }, [setActiveSection])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (params.get('addNew') !== 'true') {
            return
        }
        setCreatingApp(true)
        params.delete('addNew')
        const search = params.toString()
        history.replace(`${location.pathname}${search ? `?${search}` : ''}`)
    }, [location.search, location.pathname, history])

    const accountNames = useMemo<Record<string, string>>(
        () =>
            (user?.accounts ?? []).reduce<Record<string, string>>((acc, account) => {
                acc[account.slug] = account.name
                return acc
            }, {}),
        [user],
    )

    const activeAccountName = activeAccount ? accountNames[activeAccount] : null
    const atLimit = apps.length >= SERVICE_APP_LIMIT
    const canCreate = !atLimit && !!activeAccount && !!activeAccountName

    const handleCreate = async (app: NewServiceApp) => {
        try {
            const created = await createServiceApp(app).unwrap()
            setCreatingApp(false)
            setRevealed({ app: created, reason: 'created' })
        } catch (error) {
            showErrorMessage(errorMessage(error, t('settings.personalSettings.serviceApps.createFailed')))
        }
    }

    const handleRotate = async (app: ServiceApp) => {
        try {
            const rotated = await rotateSecret(app.clientId).unwrap()
            setRevealed({ app: rotated, reason: 'rotated' })
        } catch (error) {
            showErrorMessage(errorMessage(error, t('settings.personalSettings.serviceApps.rotateFailed')))
        }
    }

    const handleRevoke = async (app: ServiceApp) => {
        try {
            await revokeServiceApp(app.clientId).unwrap()
            showSuccessMessage(t('settings.personalSettings.serviceApps.revoked', { name: app.label || app.clientId }))
        } catch (error) {
            showErrorMessage(errorMessage(error, t('settings.personalSettings.serviceApps.revokeFailed')))
        }
    }

    const columns = useMemo<TableColumn<ServiceApp>[]>(
        () => [
            {
                Header: t('settings.personalSettings.serviceApps.columns.name'),
                accessor: 'label',
                width: '20%',
                Cell: ({ row }) => (
                    <span className="service-apps-strong" title={row.original.label || undefined}>
                        {row.original.label || t('settings.personalSettings.serviceApps.unnamedApp')}
                    </span>
                ),
            },
            {
                Header: t('settings.personalSettings.serviceApps.columns.id'),
                accessor: 'clientId',
                width: '24%',
                Cell: ({ row }) => (
                    <span className="service-apps-strong" title={row.original.clientId}>
                        {row.original.clientId}
                    </span>
                ),
            },
            {
                Header: t('settings.personalSettings.serviceApps.columns.capabilities'),
                accessor: 'permissions',
                width: '24%',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <ChipList
                        items={Array.from(new Set(Object.values(row.original.permissions).flat())).map((permission) =>
                            permission.replace(/_/g, ' '),
                        )}
                        limit={2}
                        emptyText={t('settings.personalSettings.serviceApps.parkAccessOnly')}
                    />
                ),
            },
            {
                Header: t('settings.personalSettings.serviceApps.columns.lastUsed'),
                accessor: 'lastUsedAt',
                width: '14%',
                Cell: ({ row }) => {
                    const lastUsedAt = row.original.lastUsedAt
                    if (!lastUsedAt) {
                        return (
                            <span className="service-apps-muted">
                                {t('settings.personalSettings.serviceApps.neverUsed')}
                            </span>
                        )
                    }
                    return (
                        <TooltipWrapper
                            text={format(new Date(lastUsedAt), 'd MMM yyyy, HH:mm')}
                            position="top"
                            className="service-apps-last-used"
                        >
                            <span>{formatDistanceToNow(new Date(lastUsedAt), { addSuffix: true })}</span>
                        </TooltipWrapper>
                    )
                },
            },
            {
                Header: '',
                accessor: 'createdAt',
                width: '18%',
                align: 'right',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <RowConfirmActions
                        actions={[
                            {
                                key: 'rotate',
                                iconName: 'KeyRound',
                                label: t('settings.personalSettings.serviceApps.actions.rotate'),
                                confirmLabel: t('settings.personalSettings.serviceApps.actions.rotateConfirm'),
                                onConfirm: () => handleRotate(row.original),
                            },
                            {
                                key: 'revoke',
                                iconName: 'Trash',
                                label: t('settings.personalSettings.serviceApps.actions.revoke'),
                                confirmLabel: t('settings.personalSettings.serviceApps.actions.revokeConfirm'),
                                tone: 'destructive',
                                onConfirm: () => handleRevoke(row.original),
                            },
                        ]}
                    />
                ),
            },
        ],
        [t],
    )

    return (
        <div className="service-apps-section">
            {isError ? (
                <div className="service-apps-error">
                    <LucideIcon iconName="CloudOff" size={20} />
                    <div>
                        <strong>{t('settings.personalSettings.serviceApps.loadError.title')}</strong>
                        <span>{t('settings.personalSettings.serviceApps.loadError.description')}</span>
                    </div>
                    <Button kind="action" secondary onClick={() => refetch()}>
                        {t('settings.personalSettings.serviceApps.loadError.retry')}
                    </Button>
                </div>
            ) : (
                <ReactTable
                    data={apps}
                    columns={columns}
                    loading={isLoading}
                    tableProps={{ style: { width: '100%' } }}
                    cellProps={{ style: { padding: '0.5rem 1.5rem', overflow: 'visible' } }}
                    headerProps={{ style: { padding: '0 1.5rem' } }}
                    noResultsRow={{
                        text: t('settings.personalSettings.serviceApps.emptyState'),
                    }}
                />
            )}

            {creatingApp && canCreate && activeAccount && activeAccountName && (
                <CreateServiceAppModal
                    accountSlug={activeAccount}
                    saving={creating}
                    onCancel={() => setCreatingApp(false)}
                    onCreate={handleCreate}
                />
            )}

            {revealed && (
                <SecretRevealModal
                    app={revealed.app}
                    reason={revealed.reason}
                    onDone={() => setRevealed(null)}
                />
            )}
        </div>
    )
}

export default ServiceAppsSection
